import {Alert, Button, Group, Loader, NumberInput, Paper, Stack, Text, Title} from '@mantine/core'
import {useForm} from '@mantine/form'
import {useEffect} from 'react'

import {formatBusinessTimestamp} from 'src/api/business-time'

import {EquipmentYardsSection} from '../equipment-yards/equipment-yards-section'
import {formatPrice} from '../lib/format-price'
import {parseNumberInputValue} from '../lib/parse-number-input-value'
import {ServiceTypesSection} from './service-types-section'
import {
    useCreateTransportationRatePolicy,
    useTransportationRatePolicy,
} from './use-transportation-rate-policy'

type TransportationRatePolicyFormValues = {
    minimumHourlyRate: number | ''
    defaultHourlyRate: number | ''
}

function validateTransportationRatePolicyForm(values: TransportationRatePolicyFormValues) {
    const errors: Partial<Record<keyof TransportationRatePolicyFormValues, string>> = {}

    if (values.minimumHourlyRate === '') {
        errors.minimumHourlyRate = 'Minimum hourly rate is required'
    } else if (values.minimumHourlyRate < 0) {
        errors.minimumHourlyRate = 'Minimum hourly rate must not be negative'
    }

    if (values.defaultHourlyRate !== '' && values.defaultHourlyRate < 0) {
        errors.defaultHourlyRate = 'Default hourly rate must not be negative'
    }

    return errors
}

function formValuesFromPolicy(
    policy: NonNullable<ReturnType<typeof useTransportationRatePolicy>['data']>,
): TransportationRatePolicyFormValues {
    return {
        minimumHourlyRate: policy.minimumHourlyRate,
        defaultHourlyRate: policy.defaultHourlyRate ?? '',
    }
}

export function SettingsPage() {
    const {data: policy, isLoading, isError, error, refetch} = useTransportationRatePolicy()
    const createPolicy = useCreateTransportationRatePolicy()

    const form = useForm<TransportationRatePolicyFormValues>({
        initialValues: {
            minimumHourlyRate: '',
            defaultHourlyRate: '',
        },
        validate: validateTransportationRatePolicyForm,
    })

    useEffect(() => {
        if (policy) {
            form.setValues(formValuesFromPolicy(policy))
        }
    }, [policy])

    async function handleSave() {
        const validation = form.validate()
        if (validation.hasErrors) {
            return
        }

        const minimumHourlyRate = form.values.minimumHourlyRate
        if (minimumHourlyRate === '') {
            return
        }

        const defaultHourlyRate =
            form.values.defaultHourlyRate === '' ? null : form.values.defaultHourlyRate

        await createPolicy.mutateAsync({
            minimumHourlyRate,
            defaultHourlyRate,
        })
    }

    if (isLoading) {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        )
    }

    if (isError) {
        return (
            <Stack gap="md">
                <Title order={1}>Settings</Title>
                <Alert color="red" title="Could not load settings">
                    {error instanceof Error ? error.message : 'Something went wrong.'}
                </Alert>
                <Button onClick={() => void refetch()} variant="light">
                    Retry
                </Button>
            </Stack>
        )
    }

    return (
        <Stack gap="lg">
            <Stack gap={4}>
                <Title order={1}>Settings</Title>
                <Text c="dimmed">Manage global defaults and policies.</Text>
            </Stack>

            <Paper withBorder p="md" radius="md">
                <Stack gap="md">
                    <Stack gap={4}>
                        <Title order={3}>Transportation rate policy</Title>
                        <Text size="sm" c="dimmed">
                            The minimum rate is the floor below which hourly transportation rates
                            require manager approval. The default rate pre-fills new quotes.
                        </Text>
                        {policy && (
                            <Text size="sm" c="dimmed">
                                Last updated{' '}
                                {formatBusinessTimestamp(policy.createdAt, 'MMM d, yyyy h:mm a')}
                            </Text>
                        )}
                    </Stack>

                    {createPolicy.isError && (
                        <Alert color="red" title="Could not save transportation rate policy">
                            {createPolicy.error instanceof Error
                                ? createPolicy.error.message
                                : 'Something went wrong.'}
                        </Alert>
                    )}

                    <NumberInput
                        label="Minimum hourly rate"
                        description="Rates below this require manager approval"
                        min={0}
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale
                        withAsterisk
                        value={form.values.minimumHourlyRate}
                        onChange={(value) =>
                            form.setFieldValue(
                                'minimumHourlyRate',
                                parseNumberInputValue(value) ?? '',
                            )
                        }
                        error={form.errors.minimumHourlyRate}
                    />

                    <NumberInput
                        label="Default hourly rate"
                        description="Optional rate used to pre-fill new hourly transportation quotes"
                        min={0}
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale
                        value={form.values.defaultHourlyRate}
                        onChange={(value) =>
                            form.setFieldValue(
                                'defaultHourlyRate',
                                parseNumberInputValue(value) ?? '',
                            )
                        }
                        error={form.errors.defaultHourlyRate}
                    />

                    {policy && (
                        <Stack gap="xs">
                            <Text size="sm" fw={500}>
                                Current active policy
                            </Text>
                            <Text size="sm">
                                Minimum: {formatPrice(policy.minimumHourlyRate)}/hr
                            </Text>
                            <Text size="sm">
                                Default:{' '}
                                {policy.defaultHourlyRate != null
                                    ? `${formatPrice(policy.defaultHourlyRate)}/hr`
                                    : 'Not set'}
                            </Text>
                        </Stack>
                    )}

                    <Group justify="flex-end">
                        <Button onClick={() => void handleSave()} loading={createPolicy.isPending}>
                            Save changes
                        </Button>
                    </Group>
                </Stack>
            </Paper>

            <ServiceTypesSection />
            <EquipmentYardsSection />
        </Stack>
    )
}
